import React, { useState } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';

function Root() {
  const [waitingWorker, setWaitingWorker] = useState(null);
  const [updating, setUpdating] = useState(false);
  const reloadedRef = React.useRef(false);
  const userTapRef = React.useRef(false);     // Update now was pressed in THIS tab
  const deferredRef = React.useRef(false);    // the new worker took over while this tab was in front — reload when it goes behind

  React.useEffect(() => {
    const handler = e => setWaitingWorker(e.detail);
    window.addEventListener('swUpdate', handler);
    return () => window.removeEventListener('swUpdate', handler);
  }, []);

  // A worker can already be waiting when the page loads — it installed while the tab
  // was closed, or a previous handover never completed. CRA only announces the ones
  // that finish installing while you are watching, so nothing would ever offer it.
  //
  // And the other way round: the banner used to stay up after the update had gone
  // through (Ryan's Mac, Sep 29 2026) — the waiting reference was never cleared once
  // that worker had activated or been thrown away. So re-read the registration on
  // load, every half minute and whenever the tab comes back, and follow the worker's
  // own state: no worker waiting → no banner.
  React.useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;
    const sync = () => navigator.serviceWorker.getRegistration()
      .then(reg => setWaitingWorker(reg && reg.waiting ? reg.waiting : null))
      .catch(() => {});
    sync();
    const t = setInterval(sync, 30 * 1000);
    const onVisible = () => { if (document.visibilityState === 'visible') sync(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', onVisible); };
  }, []);
  React.useEffect(() => {
    if (!waitingWorker || typeof waitingWorker.addEventListener !== 'function') return undefined;
    const onState = () => { if (waitingWorker.state === 'activated' || waitingWorker.state === 'redundant') setWaitingWorker(w => (w === waitingWorker ? null : w)); };
    onState();
    waitingWorker.addEventListener('statechange', onState);
    return () => waitingWorker.removeEventListener('statechange', onState);
  }, [waitingWorker]);

  // Reload exactly once, when the new service worker takes control. Reloading
  // here (rather than right after postMessage) avoids showing stale content or
  // needing a second refresh.
  function reloadOnce() {
    if (reloadedRef.current) return;
    reloadedRef.current = true;
    window.location.reload();
    // Safari's Dock app has been seen to swallow a reload (Ryan's Mac, Oct 3 2026). If we
    // are still here a few seconds later, try the other way, and let a later attempt through.
    setTimeout(() => { try { window.location.assign(window.location.href); } catch { /* nothing */ } reloadedRef.current = false; }, 4000);
  }

  // When is it safe to swap builds in the FOREGROUND? Not while someone is typing or
  // mid-tap: no pointer/key input for a while, and nothing focused that holds text.
  const lastInputRef = React.useRef(Date.now());
  const autoRef = React.useRef(false);        // an automatic foreground update is in flight — reload when the worker takes over
  React.useEffect(() => {
    const touch = () => { lastInputRef.current = Date.now(); };
    ['pointerdown', 'keydown', 'touchstart'].forEach(ev => window.addEventListener(ev, touch, { passive: true, capture: true }));
    return () => ['pointerdown', 'keydown', 'touchstart'].forEach(ev => window.removeEventListener(ev, touch, { capture: true }));
  }, []);
  const typing = () => { const el = document.activeElement; return !!el && (el.tagName === 'TEXTAREA' || (el.tagName === 'INPUT' && !/^(button|submit|checkbox|radio|range|file)$/i.test(el.type || '')) || el.isContentEditable); };

  // controllerchange fires in EVERY open tab the moment any one of them lets the new
  // worker in (its own background auto-update, or someone pressing Update now in
  // another window). Reloading on the spot yanked the tab someone was working in —
  // Ryan's Mac, with the app open in more than one tab, 'restarted' on every deploy
  // (Sep 30 2026). Now a tab that is in front and did not ask just notes it, and
  // reloads the next time it goes behind. The old code keeps running fine until then.
  React.useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const onControl = () => {
      if (userTapRef.current || autoRef.current || document.visibilityState === 'hidden') reloadOnce();
      else deferredRef.current = true;
    };
    const onHide = () => { if (deferredRef.current && document.visibilityState === 'hidden') reloadOnce(); };
    navigator.serviceWorker.addEventListener('controllerchange', onControl);
    document.addEventListener('visibilitychange', onHide);
    return () => { navigator.serviceWorker.removeEventListener('controllerchange', onControl); document.removeEventListener('visibilitychange', onHide); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Tell the waiting worker to activate; the controllerchange listener above reloads
  // once it has taken over.
  //
  // It does not always take over. The worker reference can be stale, the message can
  // land on one that has already activated, and with more than one tab open the
  // handover can simply not happen — and then the banner sat there, doing nothing
  // each time it was pressed. So: re-read the registration at the moment of the tap,
  // and if nothing has happened shortly after, reload anyway.
  async function applyUpdate({ userInitiated = false, auto = false } = {}) {
    if (userInitiated) { setUpdating(true); userTapRef.current = true; }
    if (auto) autoRef.current = true;
    let w = waitingWorker;
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg && reg.waiting) w = reg.waiting;
    } catch { /* fall back to the reference we already have */ }
    w?.postMessage({ type: 'SKIP_WAITING' });
    setTimeout(() => {
      // Never yank the page out from under someone mid-tap: the fallback only fires
      // for a tap they made themselves, a quiet moment we picked, or while the app is
      // in the background.
      if (userInitiated || auto || document.visibilityState === 'hidden') reloadOnce();
    }, 2500);
  }

  // The Mac in front all day (Ryan, Oct 3 2026): a window that never goes behind never
  // got an update on its own, so every deploy meant a banner and an Update now — or a
  // restart when Safari's handover stuck. Now a waiting update also goes in at the two
  // moments nobody notices: on the next move between pages, and after three quiet
  // minutes with nothing being typed. The banner only shows once an update has sat
  // unapplied for five minutes.
  const waitingSinceRef = React.useRef(0);
  const [bannerDue, setBannerDue] = useState(false);
  React.useEffect(() => {
    if (!waitingWorker) { waitingSinceRef.current = 0; setBannerDue(false); return undefined; }
    if (!waitingSinceRef.current) waitingSinceRef.current = Date.now();
    const quiet = () => Date.now() - lastInputRef.current > 3 * 60 * 1000 && !typing();
    // One automatic try per ten minutes: a worker that will not take over (it happens in
    // Safari) must not turn every page move into a reload.
    const auto = () => {
      let last = 0; try { last = Number(sessionStorage.getItem('titan_auto_update_at') || 0); } catch { /* nothing */ }
      if (autoRef.current || Date.now() - last < 10 * 60 * 1000) return;
      try { sessionStorage.setItem('titan_auto_update_at', String(Date.now())); } catch { /* nothing */ }
      applyUpdate({ auto: true });
    };
    const tick = () => {
      if (Date.now() - waitingSinceRef.current > 5 * 60 * 1000) setBannerDue(true);
      if (document.visibilityState === 'visible' && quiet()) auto();
    };
    const t = setInterval(tick, 20 * 1000);
    // A move between pages: the new URL is already in the address bar, so a reload lands
    // on it. Skip it only if someone is in the middle of typing (unlikely on a navigation).
    const onNav = () => { if (document.visibilityState === 'visible' && !typing()) auto(); };
    window.addEventListener('titan:navigated', onNav);
    window.addEventListener('popstate', onNav);
    return () => { clearInterval(t); window.removeEventListener('titan:navigated', onNav); window.removeEventListener('popstate', onNav); };
  }, [waitingWorker]); // eslint-disable-line react-hooks/exhaustive-deps

  // Install updates on their own. Waiting for someone to notice a banner meant
  // phones sat on an old build for days ("the new tile never showed up"). When a
  // new version is ready we apply it the moment the app is BACKGROUNDED, or when
  // it's reopened — never mid-tap, so nobody loses what they were typing. The
  // banner stays for anyone who wants it immediately.
  React.useEffect(() => {
    if (!waitingWorker) return undefined;
    const applyWhenSafe = () => {
      if (document.visibilityState === 'hidden') applyUpdate();
    };
    document.addEventListener('visibilitychange', applyWhenSafe);
    // Already backgrounded when the update landed → take it now.
    if (document.visibilityState === 'hidden') applyUpdate();
    return () => document.removeEventListener('visibilitychange', applyWhenSafe);
  }, [waitingWorker]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <App />
      {waitingWorker && (bannerDue || updating) && (
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 99999,
          background: '#1a1a1a', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 20px',
          paddingBottom: 'calc(14px + env(safe-area-inset-bottom))',
          boxShadow: '0 -2px 12px rgba(0,0,0,0.4)',
        }}>
          <span style={{ fontSize: 14 }}>{updating ? 'Updating…' : 'A new version is available.'}</span>
          <button
            onClick={() => applyUpdate({ userInitiated: true })}
            disabled={updating}
            style={{ background: updating ? '#7a7a7a' : '#e87722', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 14, fontWeight: 700, cursor: updating ? 'default' : 'pointer' }}
          >{updating ? 'Please wait…' : 'Update now'}</button>
        </div>
      )}
    </>
  );
}

// Say when the app moves between pages (React Router goes through pushState), so a
// waiting update can go in at that moment — see Root above.
(() => {
  const orig = window.history.pushState;
  window.history.pushState = function (...args) {
    const r = orig.apply(this, args);
    try { window.dispatchEvent(new Event('titan:navigated')); } catch { /* nothing */ }
    return r;
  };
})();

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<React.StrictMode><Root /></React.StrictMode>);

serviceWorkerRegistration.register({
  onUpdate: registration => {
    // Notify the Root component that a new SW is waiting
    const event = new CustomEvent('swUpdate', { detail: registration.waiting });
    window.dispatchEvent(event);
  },
});
